import Add from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import get from "lodash/get";
import includes from "lodash/includes";
import orderBy from "lodash/orderBy";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { Search, Sorty } from "../../../common/js/icons";
import CompanyForm from "./CompanyForm";
import schema from "./companyTable";
import { company_type_options } from "./constants";

const StyledPageToolbar = styled(Grid)(() => ({
    marginBottom: "20px",
    marginTop: "20px",
}));

const StyledAlternateContent = styled("div")(() => ({
    margin: "auto",
    textAlign: "center",
    marginTop: "6em",
    marginBottom: "6em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
}));

const StyledLb = styled(Link)(() => ({
    textDecoration: "none",
    color: "#2C99D2",
    fontWeight: 600,
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
}));

const StyledSortable = styled("span")(() => ({
    paddingRight: "5px",
    paddingBottom: "3px",
}));

const StyledLoadingSpinner = styled("div")(() => ({
    position: "absolute",
    left: "50%",
    top: "60%",
    transform: "translate(-50%, -50%)",
    marginTop: "100px",
}));

const StyledCompanyCheckbox = styled(Checkbox, {
    shouldForwardProp: (prop) => prop !== "selected",
})(({ selected }) => ({
    "& svg": {
        fontSize: "17px",
        color: selected ? "inherit" : "rgba(0, 0, 0, 0.34) !important",
    },
}));

const Companies = (props) => {
    const {
        addCompany,
        columnToSort,
        companies,
        companySearchType,
        companyTab,
        filterQuery,
        getCompany,
        handleChangeCompanySearchType,
        handleCheckboxClick,
        handleClearCheckboxes,
        handleFilter,
        handleInputChange,
        handleSearchCompanies,
        handleSort,
        handleSubmit,
        isLoading,
        selectedApprovedCompanies,
        selectedUnapprovedCompanies,
        sortDirection,
        switchTab,
        userProfile,
    } = props;

    const navigate = useNavigate();
    const [searchRun, setSearchRun] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
    const [companyFormDialogOpen, setCompanyFormDialogOpen] = useState(false);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [parentCompanyTypeFilter, setParentCompanyTypeFilter] = useState(["BUSINESS"]);

    const onKeyPress = (event) => {
        if (event.key === "Enter") {
            setSearchRun(true);
            handleSearchCompanies();
            handleClearCheckboxes();
        }
    };

    const showHideErrorDialog = (currState) => {
        setErrorDialogOpen(!currState);
    };

    const showHideMergeDialog = (currState) => {
        setMergeDialogOpen(!currState);
    };

    const switchToCompanyFormDialog = () => {
        setMergeDialogOpen(false);
        setCompanyFormDialogOpen(true);
    };

    const showHideCompanyFormDialog = (currState) => {
        setCompanyFormDialogOpen(!currState);
    };

    const handleMergeCompanies = (data) => {
        const selectedCompanies = [];
        let counter = 0;
        data.forEach((company) => {
            if (companyTab === "approved_companies") {
                if (selectedApprovedCompanies.get(company.companyKey)) {
                    selectedCompanies.push(company.companyKey);
                    counter++;
                }
            } else if (companyTab === "unapproved_companies") {
                if (selectedUnapprovedCompanies.get(company.companyKey)) {
                    selectedCompanies.push(company.companyKey);
                    counter++;
                }
            }
            if (counter === 1) {
                getCompany(company.companyKey);
            }
        });

        setSelectedCompanies(selectedCompanies);

        if (selectedCompanies.length < 2) {
            showHideErrorDialog(errorDialogOpen);
        } else {
            showHideMergeDialog(mergeDialogOpen);
        }
    };

    const handleChangeParentCompanyType = (value) => {
        setParentCompanyTypeFilter(value.split());
    };

    const handleSubmitLocal = () => {
        handleInputChange("company.companyKey", null);
        handleSubmit(selectedCompanies);
        setCompanyFormDialogOpen(false);
    };

    const sortedData = orderBy(
        filterQuery && companies
            ? companies.filter((x) => schema.filterByQuery(x, schema.columnsToQueryFilter, filterQuery))
            : companies,
        columnToSort,
        sortDirection
    );

    if (!includes(["dataAdmin", "dataEntry"], get(userProfile, "role", null))) {
        return null;
    }
    const handleAddCompany = () => {
        addCompany();
        navigate("/company");
    };

    return (
        <div>
            <StyledPageToolbar container sx={{justifyContent: "space-between", alignItems: "center"}}>
                <div>
                    <h2 className="page-title">Company Admin</h2>
                </div>
                <div>
                    <Button onClick={() => handleMergeCompanies(sortedData)} variant="outlined" color="primary">
                        Merge Duplicates
                    </Button>
                    <Button onClick={() => handleAddCompany()} variant="contained" color="primary">
                        <Add fill="#ffffff" />
                        Add Company
                    </Button>
                </div>
            </StyledPageToolbar>
            <Grid container sx={{
                alignItems: "center"
            }}>
                <Grid style={{ width: "100%" }}>
                    <div className="simPaper">
                        <Grid container spacing={0} sx={{
                            justifyContent: "space-between"
                        }}>
                            <Grid>
                                <Tabs value={companyTab} onChange={switchTab}>
                                    <Tab value="unapproved_companies" label="Unapproved Companies" />
                                    <Tab value="approved_companies" label="Approved Companies" />
                                </Tabs>
                            </Grid>
                            <Grid container sx={{
                                justifyContent: "flex-end"
                            }}>
                                <Grid>
                                    <BSInput
                                        value={filterQuery}
                                        placeholder="Search Companies"
                                        errorPlaceholder="hideIt"
                                        startAdornment={<Search />}
                                        onChange={(event) => {
                                            handleFilter(event.target.value);
                                        }}
                                        onKeyPress={(e) => onKeyPress(e)}
                                        extraFormControlStyles={{ marginTop: 0 }}
                                    />
                                </Grid>
                                <Grid style={{ marginRight: "10px" }}>
                                    <BSSelect
                                        label="Company Type"
                                        extraInputStyles={{ marginTop: "0px", width: "150px" }}
                                        id="companySearchType"
                                        value={companySearchType}
                                        options={company_type_options}
                                        onChange={(e) => handleChangeCompanySearchType(e.target.value)}
                                        helperText={"*Type Required"}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <div>
                            <div style={{ display: "flex" }}>
                                <div style={{ minHeight: "60vh", maxWidth: "100%", flex: "1 1 0" }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell></TableCell>
                                                {schema.viewHeaders["default_headers"].map((x, i) => (
                                                    <TableCell key={`thc-${i}`}>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                width: x.name === "Some Company Header" ? "185px" : "",
                                                            }}
                                                            onClick={() => handleSort(x.sort ? x.sort : x.prop)}
                                                        >
                                                            <StyledSortable>{x.name}</StyledSortable>
                                                            {x.name && (
                                                                <div>
                                                                    <Sorty />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {isLoading && searchRun ? (
                                                <StyledLoadingSpinner>
                                                    <CircularProgress color="primary" />
                                                </StyledLoadingSpinner>
                                            ) : (
                                                sortedData.map((company, i) =>
                                                    row(
                                                        company,
                                                        i,
                                                        companyTab,
                                                        isLoading,
                                                        handleCheckboxClick,
                                                        selectedApprovedCompanies,
                                                        selectedUnapprovedCompanies
                                                    )
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                    {!searchRun ? (
                                        <div>
                                            <StyledAlternateContent>
                                                Please apply search criteria
                                            </StyledAlternateContent>
                                        </div>
                                    ) : (
                                        ""
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>
            </Grid>
            <Dialog
                open={errorDialogOpen}
                onClose={showHideErrorDialog}
                aria-labelledby="merge-error"
                aria-describedby="merge-error-dialog"
            >
                <DialogTitle id="merge-error">{"Cannot Merge"}</DialogTitle>
                <DialogContent id="alert-dialog-description">
                    At least two companies must be selected in order to perform a merge
                </DialogContent>
                <DialogActions>
                    <Button onClick={showHideErrorDialog} color="default">
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={mergeDialogOpen}
                onClose={() => showHideMergeDialog()}
                aria-labelledby="merge-prompt"
                aria-describedby="merge-prompt-dialog"
            >
                <DialogTitle id="merge-prompt">{"Merge duplicate companies?"}</DialogTitle>
                <DialogContent id="alert-dialog-description">
                    Are you sure you want to merge the selected companies?
                    {<br />}
                    The merged companies cannot be recovered once they have been deleted.
                    {<br />}
                    {<br />}
                    Note that institutional owners and companies associated with multiple company types will be bypassed
                    during the merge.
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            showHideMergeDialog();
                        }}
                        color="primary"
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button onClick={switchToCompanyFormDialog} color="secondary" autoFocus variant="contained">
                        Start
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={companyFormDialogOpen}
                onClose={showHideCompanyFormDialog}
                aria-labelledby="merge-form"
                aria-describedby="merge-form-dialog"
            >
                <DialogTitle id="merge-form">{"Company Information"}</DialogTitle>
                <DialogContent>
                    <br></br>
                    <CompanyForm
                        {...props}
                        parentCompanyTypeFilter={parentCompanyTypeFilter}
                        handleChangeParentCompanyType={(value) => handleChangeParentCompanyType(value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={showHideCompanyFormDialog} color="primary" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmitLocal} color="secondary" autoFocus variant="contained">
                        Start
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const row = (
    x,
    i,
    companyTab,
    isLoading,
    handleCheckboxClick,
    selectedApprovedCompanies,
    selectedUnapprovedCompanies
) => {
    return isLoading ? null : (
        <TableRow key={`tr-${i}`} selectable="false">
            <TableCell>
                <StyledCompanyCheckbox
                    selected={
                        selectedApprovedCompanies.get(x.companyKey) === true ||
                        selectedUnapprovedCompanies.get(x.companyKey)
                    }
                    checked={
                        selectedApprovedCompanies.get(x.companyKey) === true ||
                        selectedUnapprovedCompanies.get(x.companyKey) === true
                    }
                    value={
                        selectedApprovedCompanies.get(x.companyKey) === true ||
                        selectedUnapprovedCompanies.get(x.companyKey) === true
                    }
                    color="primary"
                    onChange={() => handleCheckboxClick(x.companyKey, companyTab)}
                />
            </TableCell>
            {schema.viewHeaders["default_headers"].map((y, k) => (
                <TableCell key={`trc-${k}`} style={{ width: y.name === "Some Company Header" ? "185px" : "" }}>
                    {y.name === "LEGAL NAME" ? (
                        <StyledLb to={`/company/${x.companyKey}`}>{get(x, "legalName", "")}</StyledLb>
                    ) : y.name === "TYPE" ? (
                        y.transform(x).map((type, index) => {
                            return (
                                <span key={index}>
                                    {type}
                                    <br />
                                </span>
                            );
                        })
                    ) : y.transform ? (
                        y.transform(x)
                    ) : (
                        get(x, y.prop, "")
                    )}
                </TableCell>
            ))}
        </TableRow>
    );
};

export default Companies;
